import { isValidElement, type ReactNode } from 'react';

/**
 * Sorting for DataTable without asking every screen for sort keys: the visible text of a cell is
 * read, and a cell that is a figure (an amount, a count, a percentage, a duration) sorts as a
 * number, everything else as text in the person's language. A screen can still pass its own keys.
 */

export type SortKey = string | number | null;

/** The text a person sees in a cell (strings and numbers, through elements and arrays). */
export function nodeText(node: ReactNode): string {
  if (node === null || node === undefined || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number' || typeof node === 'bigint') return String(node);
  if (Array.isArray(node)) return node.map(nodeText).filter(Boolean).join(' ');
  if (isValidElement<{ children?: ReactNode }>(node)) return nodeText(node.props.children);
  return '';
}

// A figure with at most a short sign or currency before it and a short unit after it: "1,234.50 L",
// "-12 %", "€ 40", "9.8 h", "3". Text with words in it ("INV 12/2026", "29 Oct 2026") is not a figure.
const FIGURE = /^[^\p{L}\d]{0,3}[-−+]?\d[\d\s.,'’  ]*(?:\s*(?:[\p{L}%°]{1,4}|[\p{Sc}]))?$/u;

/** How a language writes numbers and dates, read once from Intl. */
export interface SortLocale {
  decimal: string;
  /** Short month names, lower case, without dots → month index. */
  months: Map<string, number>;
  /** The word for afternoon in 12-hour times ("pm", "e pasdites"), if the language uses one. */
  pm: string | null;
}

export function sortLocale(lang: string): SortLocale {
  const decimal = new Intl.NumberFormat(lang).formatToParts(1.5).find((p) => p.type === 'decimal')?.value ?? '.';
  const months = new Map<string, number>();
  for (let m = 0; m < 12; m++) {
    const at = new Date(Date.UTC(2026, m, 15));
    for (const month of ['short', 'long'] as const) {
      const name = new Intl.DateTimeFormat(lang, { month, timeZone: 'UTC' }).format(at).toLowerCase().replace(/\./g, '');
      months.set(name, m);
    }
  }
  const parts = new Intl.DateTimeFormat(lang, { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' }).formatToParts(new Date(Date.UTC(2026, 0, 1, 13)));
  const pm = parts.find((p) => p.type === 'dayPeriod')?.value.toLowerCase() ?? null;
  return { decimal, months, pm };
}

// "29 Oct 2026", "29 tet 2026, 01:05 e pasdites", "29 Oct 2026, 13:05" (as format.ts writes them),
// possibly followed by more text in the same cell.
const DATE = /^(\d{1,2})\s+(\p{L}+)\.?\s+(\d{4})(?:,?\s+(\d{1,2}):(\d{2}))?/u;

/** The value a cell sorts by: a number for figures and dates, lower-case text otherwise, null when empty. */
export function sortValue(text: string, loc: SortLocale = { decimal: '.', months: new Map(), pm: null }): SortKey {
  const s = text.replace(/\s+/g, ' ').trim();
  if (!s || s === '—' || s === '-') return null;
  const d = DATE.exec(s);
  const month = d ? loc.months.get(d[2]!.toLowerCase()) : undefined;
  if (d && month !== undefined) {
    let hour = d[4] ? Number(d[4]) : 0;
    if (d[4] && loc.pm && s.toLowerCase().includes(loc.pm) && hour < 12) hour += 12;
    return Date.UTC(Number(d[3]), month, Number(d[1]), hour, d[5] ? Number(d[5]) : 0);
  }
  const decimal = loc.decimal;
  if (FIGURE.test(s)) {
    const negative = /^[^\d]*[-−]/.test(s);
    const digits = s.replace(new RegExp(`[^\\d${decimal === ',' ? ',' : '.'}]`, 'g'), '').replace(decimal, '.');
    const n = Number.parseFloat(digits);
    if (Number.isFinite(n)) return negative ? -n : n;
  }
  return s.toLowerCase();
}

/** Compares two sort values: numbers before text, empty cells always last. */
export function compareKeys(a: SortKey, b: SortKey, collator: Intl.Collator): number {
  if (a === null || b === null) return a === b ? 0 : a === null ? 1 : -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  if (typeof a === 'number') return -1;
  if (typeof b === 'number') return 1;
  return collator.compare(a, b);
}
